'use strict';

const _ = require('lodash');
const GridRowRenderer = require('../GridRowRenderer');
class UnlockedGridRowRenderer extends GridRowRenderer {
    constructor(model) {
        super(model);

        this._displayAdjustTimeout = undefined;
        this._gridBodyWidth = 0;
        this._gridBodyScrollLeft = undefined;

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _createRootElement() {
        return document.createElement('unlocked-grid-row');
    }

    _registerListeners() {
        super._registerListeners();
        this._model.getGrid().on('GridResized', (this._onGridResized = this._onGridResized.bind(this)));
        this._model.getGrid().on('UserScrolled', (this._onGridUserScrolled = this._onGridUserScrolled.bind(this)));
    }

    _unregisterListeners() {
        super._unregisterListeners();
        this._model.getGrid().removeListener('GridResized', this._onGridResized);
        this._model.getGrid().removeListener('UserScrolled', this._onGridUserScrolled);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return super.render();
    }

    shutdown() {
        if (!_.isUndefined(this._displayAdjustTimeout)) {
            clearTimeout(this._displayAdjustTimeout);
        }
        this._displayAdjustTimeout = undefined;
        this._gridBodyWidth = undefined;
        this._gridBodyScrollLeft = undefined;

        return super.shutdown();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Triggered by the 'UserResized' event for each column.  See model for event binding note.
     * @param columnData.column {String} - The id of the column that has been resized
     * @param columnData.width {Number} - The width in pixels of the column
     * @param columnData.event {Event} - The mouse event that triggered the resize
     * @private
     */
    _onColumnWidth(columnData) {
        super._onColumnWidth(columnData);
        this._updateRowWidth();
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Triggered when the grid has been resized
     * @private
     */
    _onGridResized(/*eventData*/) {
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Triggered when the grid has been scrolled by the user
     * @param eventData.scrollTop {Number} - The pixel value of the distance the grid body has been scrolled from the top
     * @param eventData.scrollLeft {Number} - The pixel value of the distance the grid body has been scrolled from the left
     * @param eventData.event {Event} - The DOM scroll event
     * @private
     */
    _onGridUserScrolled(eventData) {
        this._gridBodyScrollLeft = eventData.scrollLeft;
        this._adjustVirtualRenderingDisplay();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getColumns() {
        return this._model.getUnlockedColumns();
    }

    /**
     * Build the row display from the entity
     * @param entity {object} New entity that contains data for the current row
     * @private
     */
    _setEntity(entity) {
        super._setEntity(entity);
        this._updateRowWidth();
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Show/hide cells if they are not displaying in the grid bodies scrollable area.  Use a buffer
     * of 20 rems on either side of the scrollable area as a boundary for the show/hide action to occur.
     * @private
     */
    _adjustVirtualRenderingDisplay() {
        if (_.isUndefined(this._displayAdjustTimeout)) {
            this._displayAdjustTimeout = setTimeout(() => {
                this._captureGridBodyWidth();
                if (this._gridBodyWidth !== 0) {
                    let unlockedColumns = this._model.getUnlockedColumns(),
                        offScreenBufferWidth = 200,
                        rowWidthTotal = 0,
                        paddingLeft = 0,
                        gridBodyScrollLeft = _.isUndefined(this._gridBodyScrollLeft) ? 0 : this._gridBodyScrollLeft,
                        columnComponents = this._model.getColumnComponents();
                    unlockedColumns.forEach(columnKey => {
                        if (columnComponents[columnKey].getShown()) {
                            let cell = this._cells[columnKey],
                                cellWidth = cell.getWidth(),
                                offScreenLeft = cell.getOffScreenLeft();
                            // Off screen to the left
                            if (gridBodyScrollLeft > rowWidthTotal + cellWidth + offScreenBufferWidth) {
                                cell.setShown(false);
                                cell.setOffScreenLeft(true);
                                paddingLeft += cell.getWidth();
                                // On screen
                            } else {
                                if (offScreenLeft === true) {
                                    paddingLeft -= cell.getWidth();
                                }
                                cell.setShown(true);
                                cell.setOffScreenLeft(false);
                            }
                            rowWidthTotal += cellWidth;
                        }
                    });
                    this._rootElem.style.paddingLeft = paddingLeft / 10 + 'rem';
                }
                this._displayAdjustTimeout = undefined;
            }, this._model.getTimeoutDuration());
        }
    }

    /**
     * Rebuild the row with the new column order
     * @param lockedColumns {Array} - Array of Strings (column ids) representing the order of the locked columns
     * @private
     */
    _setLockedColumns(lockedColumns) {
        super._setLockedColumns(lockedColumns);

        this._updateRowWidth();
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Set the row width
     * @param newVal {Number} - Number in pixels of the width of the row
     * @private
     */
    _setRowWidth(newVal) {
        this._rootElem.style.width = newVal / 10 + 'rem';
    }

    /**
     * Capture the grid body width to use in virtual rendering calculations
     * @private
     */
    _captureGridBodyWidth() {
        let scrollableGridBody = this._rootElem.closest('grid-body-container');
        if (scrollableGridBody !== null) {
            this._gridBodyWidth = scrollableGridBody.clientWidth;
        }
    }

    /**
     * Adjust row width
     * @private
     */
    _updateRowWidth() {
        let unlockedColumns = this._model.getUnlockedColumns(),
            columnComponents = this._model.getColumnComponents(),
            rowWidth = 0;
        unlockedColumns.forEach(columnKey => {
            if (columnComponents[columnKey].getShown()) {
                rowWidth += this._cells[columnKey].getWidth();
            }
        });
        this._setRowWidth(rowWidth);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = UnlockedGridRowRenderer;
